import { useEffect } from "react";
import { Col, Row, Typography } from "antd";
import styles from "./DesignCAD.module.scss";

const { Title, Paragraph } = Typography;

function DesignCAD() {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <div className={styles.page}>
      {/* Header */}
      <div className={styles.header}>
        <span className={styles.eyebrow}>The Diamond Counter</span>
        <Title level={1} className={styles.pageTitle}>Design & CAD</Title>
        <div className={styles.headingLine} />
      </div>

      {/* Intro */}
      <div className={styles.intro}>
        <Paragraph className={styles.introText}>
          From concept to creation, our expert design team brings your vision to life using
          cutting-edge CAD technology. Whether you have a sketch, an idea, or simply an
          inspiration — we craft bespoke jewellery that is uniquely yours.
        </Paragraph>
      </div>

      {/* Process Steps */}
      <div className={styles.processSection}>
        <Row justify="center">
          <Col xs={22} xl={20}>
            <div className={styles.sectionHeading}>
              <span className={styles.eyebrow}>How It Works</span>
              <h2 className={styles.sectionTitle}>Our Design Process</h2>
              <div className={styles.sectionLine} />
            </div>

            <div className={styles.stepsGrid}>
              <div className={styles.stepCard}>
                <div className={styles.stepNumber}>1</div>
                <h3 className={styles.stepTitle}>Consultation</h3>
                <p className={styles.stepDesc}>
                  Share your ideas with our design specialists. We'll discuss your vision,
                  budget, and preferences to understand exactly what you're looking for.
                </p>
              </div>

              <div className={styles.stepCard}>
                <div className={styles.stepNumber}>2</div>
                <h3 className={styles.stepTitle}>Design & CAD</h3>
                <p className={styles.stepDesc}>
                  Our designers create detailed 3D CAD models of your piece, allowing you to
                  see photorealistic renders and make adjustments before production.
                </p>
              </div>

              <div className={styles.stepCard}>
                <div className={styles.stepNumber}>3</div>
                <h3 className={styles.stepTitle}>Stone Selection</h3>
                <p className={styles.stepDesc}>
                  Choose from our curated collection of ethically sourced diamonds and
                  gemstones, hand-picked for quality, brilliance, and value.
                </p>
              </div>

              <div className={styles.stepCard}>
                <div className={styles.stepNumber}>4</div>
                <h3 className={styles.stepTitle}>Crafting</h3>
                <p className={styles.stepDesc}>
                  Your design is brought to life by our master craftsmen using traditional
                  techniques combined with modern precision manufacturing.
                </p>
              </div>

              <div className={styles.stepCard}>
                <div className={styles.stepNumber}>5</div>
                <h3 className={styles.stepTitle}>Delivery</h3>
                <p className={styles.stepDesc}>
                  Your finished piece is quality-checked and beautifully
                  presented — ready to be treasured for a lifetime.
                </p>
              </div>
            </div>
          </Col>
        </Row>
      </div>

      {/* Services */}
      <div className={styles.servicesSection}>
        <div className={styles.servicesOverlay}>
          <Row justify="center">
            <Col xs={22} xl={20}>
              <div className={styles.sectionHeadingLight}>
                <span className={styles.eyebrow}>What We Offer</span>
                <h2 className={styles.sectionTitleLight}>Our Services</h2>
                <div className={styles.sectionLine} />
              </div>

              <Row gutter={[32, 32]} justify="center">
                <Col xs={24} md={8}>
                  <div className={styles.serviceCard}>
                    <div className={styles.serviceIconWrap}>
                      {/* Cube / 3D modelling icon */}
                      <svg width="36" height="36" viewBox="0 0 24 24" fill="none" stroke="#C5A028" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round">
                        <path d="M21 16V8a2 2 0 0 0-1-1.73l-7-4a2 2 0 0 0-2 0l-7 4A2 2 0 0 0 3 8v8a2 2 0 0 0 1 1.73l7 4a2 2 0 0 0 2 0l7-4A2 2 0 0 0 21 16z"/>
                        <polyline points="3.27 6.96 12 12.01 20.73 6.96"/>
                        <line x1="12" y1="22.08" x2="12" y2="12"/>
                      </svg>
                    </div>
                    <h3 className={styles.serviceTitle}>3D CAD Modelling</h3>
                    <div className={styles.serviceLine} />
                    <p className={styles.serviceDesc}>
                      Full 3D design of rings, pendants, bracelets, and more — with
                      photorealistic renders so you can see your piece before it's made.
                    </p>
                  </div>
                </Col>
                <Col xs={24} md={8}>
                  <div className={styles.serviceCard}>
                    <div className={styles.serviceIconWrap}>
                      {/* Diamond / bespoke icon */}
                      <svg width="36" height="36" viewBox="0 0 24 24" fill="none" stroke="#C5A028" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round">
                        <path d="M6 3h12l4 6-10 13L2 9z"/>
                        <path d="M2 9h20"/>
                        <path d="M10 3l-4 6 6 13 6-13-4-6"/>
                      </svg>
                    </div>
                    <h3 className={styles.serviceTitle}>Bespoke Jewellery</h3>
                    <div className={styles.serviceLine} />
                    <p className={styles.serviceDesc}>
                      Commission a one-of-a-kind piece tailored to your exact specifications —
                      from engagement rings to statement necklaces.
                    </p>
                  </div>
                </Col>
                <Col xs={24} md={8}>
                  <div className={styles.serviceCard}>
                    <div className={styles.serviceIconWrap}>
                      {/* Refresh / restoration icon */}
                      <svg width="36" height="36" viewBox="0 0 24 24" fill="none" stroke="#C5A028" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round">
                        <path d="M23 4v6h-6"/>
                        <path d="M1 20v-6h6"/>
                        <path d="M3.51 9a9 9 0 0 1 14.85-3.36L23 10"/>
                        <path d="M20.49 15a9 9 0 0 1-14.85 3.36L1 14"/>
                      </svg>
                    </div>
                    <h3 className={styles.serviceTitle}>Redesign & Restoration</h3>
                    <div className={styles.serviceLine} />
                    <p className={styles.serviceDesc}>
                      Breathe new life into inherited or outdated pieces. We can redesign,
                      resize, or restore jewellery to its former glory.
                    </p>
                  </div>
                </Col>
              </Row>
            </Col>
          </Row>
        </div>
      </div>

      {/* CTA */}
      <div className={styles.ctaSection}>
        <div className={styles.ctaInner}>
          <span className={styles.eyebrow}>Ready To Start?</span>
          <h2 className={styles.ctaTitle}>Let's Create Something Beautiful</h2>
          <p className={styles.ctaDesc}>
            Get in touch with our design team to begin your bespoke jewellery journey.
          </p>
          <a href="https://wa.me/447793219535" target="_blank" rel="noopener noreferrer" className={styles.ctaButton}>Contact Us</a>
        </div>
      </div>
    </div>
  );
}

export default DesignCAD;
